package com.arena_web.ui.sections.home.mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.analytics.UtmCapture
import com.arena_web.ui.components.notifications.LocalToastState
import com.arena_web.ui.components.overlay.AccountRequiredModal
import com.arena_web.ui.components.overlay.SupportPopup
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.header.components.BrandLogo
import com.arena_web.ui.sections.home.HomeAmbientOrbs
import com.arena_web.ui.sections.home.googlePlayDaysLeft
import com.arena_web.ui.sections.home.revealOnScroll
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.ThemeVars
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList

private const val URBANIST = AppTypography.PRIMARY_FONT_NAME
private const val URBANIST_FB = AppTypography.PRIMARY_FONT_FALLBACK

// Figma 9781:247386 — full mobile main page (390 × 3265).
// Top bar + safe-area chrome live in MobilePageLayout; this composable owns the
// scrollable content column under it.
@Composable
fun HomeContentMobile() {
    val t = LocalAppStrings.current
    // JOIN FREE opens the shared mobile sign-in popup (GuestWelcomeCard) —
    // the page's primary conversion action.
    var showAuth by remember { mutableStateOf(false) }
    // Soft blue orbs drift behind the content (fixed full-viewport layer,
    // z-index 0). Shared with the desktop showcase.
    HomeAmbientOrbs()
    // MobilePageLayout paints the var(--l-s3, #02040B) surface for the whole layout (chrome
    // included) — this column just hosts the scrollable content. Lifted above
    // the orb layer with `position: relative` + z-index 1.
    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "relative")
                property("z-index", "1")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
    ) {
        MobileHero(onJoin = { showAuth = true })
        MobileSpacer(88)
        StorySection(
            chip = "01 · EVENTS",
            title = t.homeStoryEventsTitle,
            subtitle = t.homeStoryEventsSub,
            // -v2 suffix busts the Bunny CDN's 30-day immutable cache — the
            // pixel-edited screenshot shares no URL with the stale original.
            mainImage = "/images/home/mobile/arena-events-v2.png",
            mainAlt = t.homeAltEventsScreen,
        )
        MobileSpacer(72)
        StorySection(
            chip = "02 · MATCH",
            title = t.homeStoryMatchTitle,
            subtitle = t.homeStoryMatchSub,
            mainImage = "/images/home/mobile/discover-people.png",
            mainAlt = t.homeAltDiscoverPeople,
            mirrored = true,
        )
        MobileSpacer(72)
        StorySection(
            chip = "03 · CLUBS",
            title = t.homeStoryClubsTitle,
            subtitle = t.homeStoryClubsSub,
            mainImage = "/images/home/mobile/clubs-list.png",
            mainAlt = t.homeAltClubsScreen,
        )
        MobileSpacer(72)
        StorySection(
            chip = "04 · MOMENTS",
            title = t.homeStoryMomentsTitle,
            subtitle = t.homeStoryMomentsSub,
            mainImage = "/images/home/mobile/feed.png",
            mainAlt = t.homeAltFeedScreen,
            mirrored = true,
        )
        MobileSpacer(72)
        StorySection(
            chip = "05 · STATS",
            title = t.homeStoryStatsTitle,
            subtitle = t.homeStoryStatsSub,
            mainImage = "/images/home/mobile/profile-overview.png",
            mainAlt = t.homeAltProfileOverview,
            // Cutout cluster mirrors ad-promo_card.png: Active & Bought card
            // grid top-right, Hub Analytics chart cascade left, radar card
            // bottom-right. Captions latin by design (ad language).
            cuts = listOf(
                StoryCut("/images/home/mobile/cut-cards.png", "", widthPx = 158, topPx = 0, edgePx = 0, rotateDeg = 0, onLeft = false, framed = false),
                StoryCut("/images/home/mobile/cut-analytics-1.png", "", widthPx = 162, topPx = 208, edgePx = 0, rotateDeg = 0, onLeft = true, label = "Hub Analytics"),
                StoryCut("/images/home/mobile/cut-analytics-2.png", "", widthPx = 168, topPx = 320, edgePx = 24, rotateDeg = 0, onLeft = true),
                StoryCut("/images/home/mobile/player-stats.png", t.homeAltPlayerStatsRadar, widthPx = 104, topPx = 388, edgePx = 2, rotateDeg = 0, onLeft = false, label = "Driven by ratings"),
            ),
        )
        MobileSpacer(80)
        StoryFinalCta(onJoin = { showAuth = true })
        MobileSpacer(56)
        MobileFooter()
        // Clear the fixed bottom navigation bar — BottomNavBar height ≈ 96 px
        // at the 390-px reference plus a 24 px breathing margin.
        MobileSpacer(120)
    }
    AccountRequiredModal(
        isOpen = showAuth,
        onClose = { showAuth = false },
        // Full navigation (not SPA) so the whole chrome — top bar avatar,
        // bottom nav badges — remounts with the fresh auth state; same
        // reload-after-login pattern as Profile/Chat.
        onSignedIn = { window.location.href = "/arena" },
    )
}

@Composable
private fun MobileSpacer(px: Int) {
    Box(modifier = Modifier.mHeight(px))
}

// =============================================================================
// Hero — multi-line tagline with pill chips on key words + store badges
// =============================================================================

@Composable
private fun MobileHero(onJoin: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            // Figma: 40 between the top bar (header ends y=107) and the tagline
            // block (y=147); MobileTopBar itself has no bottom padding.
            .mPaddingTop(40)
            .mPaddingLeft(16)
            .mPaddingRight(16)
            .mGap(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Headline shown immediately — no intro logo assembly / fly-in.
        // Ad-style headline — uppercase with a gradient accent span.
        // Semantically the page's H1 (chunks set explicit font size/
        // weight, so only the UA margin needs resetting).
        H1(
            attrs = {
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("align-items", "center")
                    property("gap", m(12))
                    property("width", "100%")
                    property("margin", "0")
                }
            }
        ) {
            HeroChunk {
                StoryHeadline(t.homeStoryHeroTitle, sizePx = 30)
            }
            HeroChunk {
                StorySubtitle(t.homeStoryHeroSub)
            }
        }

        // CTA stack appears after the headline lands: neon balls, JOIN FREE
        // pill (opens the sign-in popup), then the store badges as the
        // secondary path.
        Column(
            modifier = Modifier
                .mGap(20)
                .mPaddingTop(16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            NeonBallsRow()
            JoinFreePill(t.homeStoryJoinFree, onClick = onJoin)
            Row(
                modifier = Modifier.mGap(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                MobileAppleStoreButton()
                MobileGooglePlayButton()
            }
        }
    }
}

// Wraps a tagline chunk. `display: inline-flex` is required because the Div
// parent is a flex-wrap container — without it the wrapper would line-break
// awkwardly.
@Composable
private fun HeroChunk(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .styleModifier { property("display", "inline-flex") }
    ) { content() }
}

// =============================================================================
// Story sections — ad-carousel language: chip, headline, subtitle, composition
// =============================================================================

// One "slide" of the story: numbered chip, uppercase headline with gradient
// accent, one-line subtitle, tilted screenshot composition. Sections alternate
// phone side via [mirrored].
@Composable
private fun StorySection(
    chip: String,
    title: String,
    subtitle: String,
    mainImage: String,
    mainAlt: String,
    mirrored: Boolean = false,
    cuts: List<StoryCut> = emptyList(),
) {
    Column(
        modifier = Modifier.fillMaxWidth().mGap(12).mPaddingLeft(16).mPaddingRight(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(modifier = Modifier.revealOnScroll(0)) { StoryChip(chip) }
        Box(modifier = Modifier.fillMaxWidth().revealOnScroll(80)) {
            StoryHeadline(title, sizePx = 26)
        }
        Box(
            modifier = Modifier.fillMaxWidth().revealOnScroll(140).styleModifier {
                property("display", "flex")
                property("justify-content", "center")
            }
        ) { StorySubtitle(subtitle) }
        Box(modifier = Modifier.fillMaxWidth().revealOnScroll(200).mPaddingTop(8)) {
            StoryComposition(mainImage, mainAlt, mirrored, cuts)
        }
    }
}

// Closing block — READY TO PLAY? + balls + CTA + store badges.
@Composable
private fun StoryFinalCta(onJoin: () -> Unit) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().mGap(20).mPaddingLeft(16).mPaddingRight(16),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(modifier = Modifier.fillMaxWidth().revealOnScroll(0)) {
            StoryHeadline(t.homeStoryReadyTitle, sizePx = 30)
        }
        Box(modifier = Modifier.revealOnScroll(80)) { StorySubtitle(t.homeStoryReadySub) }
        Box(modifier = Modifier.revealOnScroll(140)) { NeonBallsRow() }
        Box(modifier = Modifier.revealOnScroll(200)) {
            JoinFreePill(t.homeStoryJoinFree, onClick = onJoin)
        }
        Row(
            modifier = Modifier.mGap(12).revealOnScroll(260),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MobileAppleStoreButton()
            MobileGooglePlayButton()
        }
    }
}

// =============================================================================
// Footer — brand, links, social, store buttons, copyright
// =============================================================================

@Composable
private fun MobileFooter() {
    val t = LocalAppStrings.current
    var showSupport by remember { mutableStateOf(false) }
    val ctx = rememberPageContext()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .mPaddingLeft(32)
            .mPaddingRight(32)
            .mGap(28),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Brand mark + tagline (Figma 9803:341755).
        Column(
            modifier = Modifier.mGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            BrandLogo(static = true)
            SpanText(
                t.homeFooterTagline,
                modifier = Modifier
                    .mFontSize(13)
                    .mLineHeight(18)
                    .mLetterSpacing(0.26)
                    .color(ThemeVars.Text2)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
                    .styleModifier { property("text-align", "center") }
            )
            // Crawlable product blurb — mirrors the desktop footer copy.
            SpanText(
                t.homeFooterBlurb,
                modifier = Modifier
                    .mFontSize(12)
                    .mLineHeight(17)
                    .color(ThemeVars.Text2)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Regular)
                    .styleModifier { property("text-align", "center") }
            )
        }

        // Stacked links.
        Column(
            modifier = Modifier.mGap(16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            MobileFooterLink(t.homeFooterAbout, path = "/about")
            MobileFooterLink(t.homeFooterTerms, path = "/terms")
            MobileFooterLink(t.homeFooterPrivacy, path = "/privacy")
            MobileFooterLink(t.homeFooterSupports) { showSupport = true }
        }

        // Social icons row (Facebook, Instagram).
        Row(
            modifier = Modifier.mGap(12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MobileSocialIcon(MobileFacebookIconSvg)
            MobileSocialIcon(MobileInstagramIconSvg, href = Constants.INSTAGRAM_URL)
        }

        // Stacked store buttons (matches the Figma footer order — Apple on top).
        Column(
            modifier = Modifier.mGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            MobileAppleStoreButton()
            MobileGooglePlayButton()
        }

        SpanText(
            t.homeFooterCopyright,
            modifier = Modifier
                .mFontSize(11)
                .mLineHeight(14)
                .color(ThemeVars.Text2)
                .fontFamily(URBANIST, URBANIST_FB)
                .fontWeight(AppTypography.Regular)
        )
    }
    SupportPopup(isOpen = showSupport, onClose = { showSupport = false })
}

@Composable
private fun MobileFooterLink(text: String, path: String? = null, onClick: (() -> Unit)? = null) {
    val ctx = rememberPageContext()
    val label: @Composable () -> Unit = {
        SpanText(
            text,
            modifier = Modifier
                .mFontSize(15)
                .mLineHeight(20)
                .color(ThemeVars.Text2)
                .fontFamily(URBANIST, URBANIST_FB)
                .fontWeight(AppTypography.Regular)
                .cursor(Cursor.Pointer)
                .thenIf(onClick != null) { Modifier.onClick { onClick!!() } }
        )
    }
    if (path != null) {
        // Real <a href> so crawlers can follow internal links; navigation
        // itself stays client-side through the router.
        A(
            href = path,
            attrs = {
                style { property("text-decoration", "none") }
                onClick {
                    it.preventDefault()
                    ctx.router.navigateTo(path)
                }
            }
        ) { label() }
    } else {
        label()
    }
}

@Composable
private fun MobileSocialIcon(svg: String, href: String? = null) {
    val icon: @Composable () -> Unit = {
        Box(
            modifier = Modifier
                .mSize(40)
                .mBorderRadius(100)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background", AppGradients.InteractiveSecondary)
                    property("border", "0.5px solid var(--arena-border-top)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
        ) {
            Box(
                modifier = Modifier
                    .mSize(20)
                    .styleModifier {
                        property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                        property("background-size", "contain")
                        property("background-repeat", "no-repeat")
                        property("background-position", "center")
                    }
            )
        }
    }
    if (href != null) {
        A(
            href = href,
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                style { property("text-decoration", "none") }
            }
        ) { icon() }
    } else {
        icon()
    }
}

// =============================================================================
// Store buttons — white pill with stacked eyebrow + name + brand glyph
// =============================================================================

// pt/ct/mt = App Store Connect campaign link (App Analytics → Campaigns, ct=web-landing).
private const val APP_STORE_URL =
    "https://apps.apple.com/app/arena-pro-play/id6790975616?pt=129172356&ct=web-landing&mt=8"
private const val GOOGLE_PLAY_BASE_URL =
    "https://play.google.com/store/apps/details?id=com.proplayarena"

/**
 * Returns the Google Play URL decorated with an Install Referrer query param
 * when first-touch UTM attribution is present, and the App Store URL (unchanged).
 */
private fun mobileStoreUrls(): Pair<String, String> {
    val referrer = UtmCapture.playReferrer()
    val playUrl = if (referrer != null) {
        "$GOOGLE_PLAY_BASE_URL&referrer=${com.arena_web.analytics.encodeURIComponent(referrer)}"
    } else {
        GOOGLE_PLAY_BASE_URL
    }
    return APP_STORE_URL to playUrl
}

@Composable
private fun MobileAppleStoreButton() {
    val t = LocalAppStrings.current
    MobileStorePill(withShadow = true, href = APP_STORE_URL, analyticsId = "store_apple") {
        MobileStoreIcon(MobileAppleIconSvg)
        Column {
            SpanText(
                t.homeStoreAppleEyebrow,
                modifier = Modifier
                    .mFontSize(8)
                    .mLineHeight(10)
                    .mLetterSpacing(0.08)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.Medium)
            )
            SpanText(
                t.homeStoreAppleName,
                modifier = Modifier
                    .mFontSize(14)
                    .mLineHeight(18)
                    .mLetterSpacing(0.07)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

@Composable
private fun MobileGooglePlayButton() {
    val t = LocalAppStrings.current
    val toastState = LocalToastState.current
    val daysLeft = googlePlayDaysLeft()
    val (_, playUrl) = mobileStoreUrls()
    MobileStorePill(
        withShadow = false,
        href = playUrl.takeIf { daysLeft <= 0 },
        onClick = { toastState.info(t.homeStoreGoogleComingSoon(daysLeft)) }
            .takeIf { daysLeft > 0 },
        analyticsId = if (daysLeft <= 0) "store_google" else "store_google_soon",
    ) {
        MobileStoreIcon(MobileGooglePlayIconSvg)
        Column {
            SpanText(
                t.homeStoreGoogleEyebrow,
                modifier = Modifier
                    .mFontSize(8)
                    .mLineHeight(10)
                    .mLetterSpacing(0.08)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
            SpanText(
                t.homeStoreGoogleName,
                modifier = Modifier
                    .mFontSize(14)
                    .mLineHeight(18)
                    .mLetterSpacing(0.07)
                    .color(AppColors.PrimaryBlack)
                    .fontFamily(URBANIST, URBANIST_FB)
                    .fontWeight(AppTypography.SemiBold)
            )
        }
    }
}

@Composable
private fun MobileStorePill(
    withShadow: Boolean,
    href: String? = null,
    onClick: (() -> Unit)? = null,
    analyticsId: String? = null,
    content: @Composable () -> Unit,
) {
    // Click tracking fires before navigation; flush() pushes the batch out
    // immediately, and the visibilitychange sendBeacon covers the switch to
    // the store app (tab backgrounds before a normal flush would land).
    val handleClick: (() -> Unit)? =
        if (analyticsId == null && onClick == null) null
        else {
            {
                analyticsId?.let {
                    com.arena_web.analytics.AnalyticsTracker.trackClick(
                        it, params = mapOf("layout" to "mobile"),
                    )
                    com.arena_web.analytics.AnalyticsTracker.flush()
                    com.arena_web.analytics.MetaCapiBeacon.trackAppDownloadForAnalyticsId(it)
                }
                onClick?.invoke()
            }
        }
    val pill: @Composable () -> Unit = {
        Row(
            modifier = Modifier
                .mWidth(152)
                .mHeight(44)
                .mBorderRadius(100)
                .backgroundColor(AppColors.PrimaryWhite)
                .mPaddingLeft(12)
                .mPaddingRight(12)
                .mGap(8)
                .cursor(Cursor.Pointer)
                .thenIf(handleClick != null) { Modifier.onClick { handleClick!!() } }
                .styleModifier {
                    // White badge: hairline must stay visible on the light bg
                    property("border", "0.5px solid var(--l-white-chip-border, rgba(255, 255, 255, 0.4))")
                    property("flex-shrink", "0")
                    if (withShadow) property("box-shadow", "0 4px 2px rgba(0, 0, 0, 0.25)")
                },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            content()
        }
    }
    if (href != null) {
        A(
            href = href,
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                style {
                    property("text-decoration", "none")
                    property("flex-shrink", "0")
                }
            }
        ) { pill() }
    } else {
        pill()
    }
}

@Composable
private fun MobileStoreIcon(svg: String) {
    Box(
        modifier = Modifier
            .mSize(24)
            .styleModifier {
                property("background-image", "url(\"data:image/svg+xml;utf8,$svg\")")
                property("background-size", "contain")
                property("background-repeat", "no-repeat")
                property("background-position", "center")
            }
    )
}

// =============================================================================
// Inline SVG icons — URL-encoded for data: URI use.
// Same artwork as HomeShowcase, duplicated here so the mobile file is
// self-contained and doesn't depend on private members of the desktop file.
// =============================================================================

private val MobileAppleIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='black'%3E" +
        "%3Cpath d='M17.564 13.234c-.026-2.6 2.122-3.847 2.22-3.91-1.21-1.77-3.094-2.012-3.764-2.04-1.602-.16-3.13.945-3.945.945-.814 0-2.072-.92-3.404-.895-1.752.026-3.368 1.018-4.27 2.586-1.82 3.158-.466 7.823 1.305 10.378.864 1.252 1.895 2.659 3.246 2.61 1.302-.052 1.794-.844 3.37-.844 1.575 0 2.018.844 3.396.815 1.404-.026 2.292-1.275 3.146-2.534.99-1.456 1.402-2.866 1.428-2.94-.032-.012-2.74-1.054-2.768-4.17zM14.943 5.4c.722-.873 1.207-2.087 1.075-3.293-1.04.042-2.297.69-3.043 1.562-.668.77-1.252 2.005-1.094 3.184 1.157.088 2.34-.586 3.062-1.453z'/%3E" +
        "%3C/svg%3E"

private val MobileGooglePlayIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E" +
        "%3Cpath fill='%2300C2FF' d='M3.46 2.46C3.18 2.72 3 3.09 3 3.5v17c0 .41.18.78.46 1.04L13.4 12 3.46 2.46z'/%3E" +
        "%3Cpath fill='%2300C853' d='M3.46 2.46l9.94 9.54 3.41-3.41L4.53 2.1c-.41-.06-.79.1-1.07.36z'/%3E" +
        "%3Cpath fill='%23FFC107' d='M16.81 8.59L13.4 12l3.41 3.41 4.04-2.27c.69-.39.69-1.39 0-1.78l-4.04-2.77z'/%3E" +
        "%3Cpath fill='%23EA4335' d='M3.46 21.54c.28.26.66.42 1.07.36l12.28-6.49L13.4 12 3.46 21.54z'/%3E" +
        "%3C/svg%3E"

private val MobileInstagramIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23B4CCF6' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E" +
        "%3Crect x='2' y='2' width='20' height='20' rx='5'/%3E" +
        "%3Cpath d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'/%3E" +
        "%3Cline x1='17.5' y1='6.5' x2='17.51' y2='6.5'/%3E" +
        "%3C/svg%3E"

private val MobileFacebookIconSvg =
    "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23B4CCF6'%3E" +
        "%3Cpath d='M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89H7.898v-2.89h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33V21.879C18.343 21.128 22 16.991 22 12z'/%3E" +
        "%3C/svg%3E"

